import { useRef, useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { RequiredLabel } from '@gonfalon/ui/RequiredLabel';
import { Disclosure, DisclosurePanel, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { AnimatePresence, m } from 'framer-motion';

import { trackFlagTargetingEvent } from '#utils/flags/flagAnalytics';
import { trackCreateApprovalRequestButtonClick } from '#utils/tracking/approvalRequest';

import type { SelectReviewerTagProps } from '../FormSection/Approvals/SelectReviewerTag';
import { SelectReviewerTag } from '../FormSection/Approvals/SelectReviewerTag';

import type { PendingChangesFormType } from './usePendingChangesFormAdapter';

import styles from './PendingChangesModal.module.css';

export interface SelectReviewersProps extends Omit<
  SelectReviewerTagProps,
  'onChange' | 'onBlur' | 'onClick' | 'description' | 'className' | 'placement'
> {
  form: PendingChangesFormType;
  approvalServiceKind?: string;
  minNumberOfApprovals: number;
  canBypassRequiredApprovals: boolean;
  bypassRequiredApprovals?: boolean;
  description?: string;
}

export function SelectReviewersField({
  form,
  isRequired,
  minNumberOfApprovals,
  approvalServiceKind,
  canBypassRequiredApprovals,
  bypassRequiredApprovals,
  description: descriptionOverride,
  ...props
}: SelectReviewersProps) {
  const added = useRef(false);
  const description =
    descriptionOverride ??
    (isRequired
      ? msg('pendingChanges.reviewersField.requiredDescription', { count: minNumberOfApprovals })
      : msg('pendingChanges.reviewersField.optionalDescription'));

  return (
    <form.AppField name="reviewers">
      {(field) => (
        <SelectReviewerTag
          className={styles.formField}
          isRequired={isRequired}
          description={description}
          onChange={(reviewers) => {
            field.handleChange(reviewers);
            if (!added.current) {
              added.current = true;
              trackFlagTargetingEvent(isRequired ? 'Reviewer Added' : 'Optional Reviewer Added', {
                bypassRequiredApprovals,
                minNumberOfApprovals,
                approvalServiceKind,
                canBypassRequiredApprovals,
                isRequired,
              });
            }
          }}
          onBlur={field.handleBlur}
          onClick={() => {
            trackCreateApprovalRequestButtonClick({
              approvalSystem: approvalServiceKind,
              resourceKind: 'FlagConfiguration',
            });
          }}
          {...props}
        />
      )}
    </form.AppField>
  );
}

export interface SelectReviewersDisclosureProps extends SelectReviewersProps {
  canBypassRequiredApprovals: boolean;
}

export function SelectReviewersDisclosure({ canBypassRequiredApprovals, ...props }: SelectReviewersDisclosureProps) {
  const { form } = props;
  const bypassRequiredApprovals = form.state.values.bypassRequiredApprovals;
  const isApprovalRequired = !bypassRequiredApprovals && props.isRequired;
  const [manualExpanded, setManualExpanded] = useState(!!(isApprovalRequired && canBypassRequiredApprovals));
  const isExpanded = bypassRequiredApprovals ? false : manualExpanded;
  const showInlineReviewersField = isApprovalRequired && !canBypassRequiredApprovals;

  if (showInlineReviewersField) {
    return (
      <SelectReviewersField
        label={msg('pendingChanges.reviewersField.reviewersLabel')}
        bypassRequiredApprovals={bypassRequiredApprovals}
        canBypassRequiredApprovals={canBypassRequiredApprovals}
        {...props}
      />
    );
  }

  return (
    <Disclosure className={styles.disclosureMenu} isExpanded={isExpanded}>
      <span slot="trigger" className={styles.disclosureTrigger}>
        <RequiredLabel htmlFor="notifyMembers" isRequired={isApprovalRequired}>
          <Message id="pendingChanges.reviewersField.requestApproval" />
        </RequiredLabel>
        <TooltipTrigger>
          <IconButton
            aria-label={
              isExpanded
                ? msg('pendingChanges.reviewersField.bypassApprovalsLabel')
                : msg('pendingChanges.reviewersField.requestReviewersLabel')
            }
            data-test-id="select-reviewers"
            icon={isExpanded ? 'minus' : 'add'}
            className={styles.disclosureTriggerIcon}
            variant="minimal"
            size="small"
            onPress={() => {
              if (isExpanded) {
                form.setFieldValue('reviewers', []);
              }
              if (canBypassRequiredApprovals) {
                form.setFieldValue('bypassRequiredApprovals', isExpanded);

                if (!isExpanded) {
                  trackFlagTargetingEvent('Bypass Approvals Clicked');
                }
              }

              setManualExpanded(!isExpanded);
              void form.validate('change');
            }}
          />

          <Tooltip>
            {isExpanded ? (
              <Message id="pendingChanges.reviewersField.saveWithoutApproval" />
            ) : (
              <Message id="pendingChanges.reviewersField.requestApproval" />
            )}
          </Tooltip>
        </TooltipTrigger>
      </span>

      <AnimatePresence>
        <DisclosurePanel className={styles.panel}>
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto', paddingBottom: 'var(--lp-spacing-400)' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
            }}
            className={styles.disclosureContent}
          >
            <SelectReviewersField
              label=""
              bypassRequiredApprovals={bypassRequiredApprovals}
              canBypassRequiredApprovals={canBypassRequiredApprovals}
              {...props}
            />
          </m.div>
        </DisclosurePanel>
      </AnimatePresence>
    </Disclosure>
  );
}
